import React, { useState, useRef, useEffect, MouseEvent } from 'react';
import { ChevronRight, Check, Plus, LogIn, Cloud, X } from 'lucide-react';

interface TimelineItem {
  id: string;
  name: string;
  ownerId?: string;
  isLocal?: boolean;
  isLocalSubscription?: boolean;
  isSubscribed?: boolean;
  color?: string | null;
}

interface TimelineSwitcherProps {
  title?: string;
  timelines?: TimelineItem[];
  currentTimelineId?: string | null;
  currentUserId?: string | null;
  onTimelineSwitch: (timelineId: string) => void;
  onCreateNew?: () => void;
  onUnsubscribe?: (timeline: TimelineItem) => void;
  showLoginPrompt?: boolean;
  onLoginClick?: () => void;
}

const TimelineSwitcher: React.FC<TimelineSwitcherProps> = ({
  title,
  timelines = [],
  currentTimelineId,
  currentUserId,
  onTimelineSwitch,
  onCreateNew,
  onUnsubscribe,
  showLoginPrompt = false,
  onLoginClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Separate timelines by type
  const ownedTimelines = timelines.filter((t) => !t.isLocal && !t.isLocalSubscription && t.ownerId === currentUserId);
  const localTimelines = timelines.filter((t) => t.isLocal);
  const subscribedTimelines = timelines.filter((t) => (t.isSubscribed && t.ownerId !== currentUserId) || t.isLocalSubscription);

  const handleTimelineClick = (timelineId: string) => {
    if (timelineId !== currentTimelineId) {
      onTimelineSwitch(timelineId);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group flex items-center gap-1 max-w-[55vw] sm:max-w-xs px-2 py-1.5 rounded-lg hover:bg-gray-200/60 dark:hover:bg-gray-700/60 transition-colors"
        title="Switch timeline"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {title !== undefined && (
          <span className="truncate text-base font-medium text-gray-900 dark:text-white">
            {title || 'Untitled Timeline'}
          </span>
        )}
        <ChevronRight
          className={`w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-transform duration-200 ${
            isOpen ? 'rotate-90' : ''
          }`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-xl z-[9999] py-1 max-h-96 overflow-y-auto">
          {/* Local timeline (for unauthenticated users) */}
          {localTimelines.length > 0 && (
            <>
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                My Timeline
              </div>
              {localTimelines.map((timeline) => (
                <button
                  key={timeline.id}
                  onClick={() => handleTimelineClick(timeline.id)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="truncate">{timeline.name}</span>
                    <span className="text-xs text-gray-500">(Local)</span>
                  </div>
                  {timeline.id === currentTimelineId && (
                    <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                  )}
                </button>
              ))}
            </>
          )}

          {/* Owned timelines (for authenticated users) */}
          {ownedTimelines.length > 0 && (
            <>
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                My Timelines
              </div>
              {ownedTimelines.map((timeline) => (
                <button
                  key={timeline.id}
                  onClick={() => handleTimelineClick(timeline.id)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {timeline.color && (
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: timeline.color }}
                      />
                    )}
                    <span className="truncate">{timeline.name}</span>
                  </div>
                  {timeline.id === currentTimelineId && (
                    <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                  )}
                </button>
              ))}
            </>
          )}

          {/* Subscribed timelines */}
          {subscribedTimelines.length > 0 && (
            <>
              <div className={`px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider ${ownedTimelines.length > 0 || localTimelines.length > 0 ? 'mt-2 border-t border-gray-200 dark:border-gray-700' : ''}`}>
                Subscribed Timelines
              </div>
              {subscribedTimelines.map((timeline) => (
                <div
                  key={timeline.id}
                  className="flex items-center justify-between px-3 py-2.5 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                >
                  <button
                    onClick={() => handleTimelineClick(timeline.id)}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left"
                  >
                    {timeline.color && (
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: timeline.color }}
                      />
                    )}
                    <span className="truncate">{timeline.name}</span>
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {timeline.id === currentTimelineId && (
                      <Check className="w-4 h-4 text-green-500 dark:text-green-400" />
                    )}
                    {onUnsubscribe && (
                      <button
                        onClick={(e: MouseEvent) => {
                          e.stopPropagation();
                          onUnsubscribe(timeline);
                        }}
                        className="p-1 text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-200/50 dark:hover:bg-gray-600/50 rounded transition-colors opacity-0 group-hover:opacity-100"
                        title="Unsubscribe from timeline"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Create new timeline (only for authenticated users) */}
          {onCreateNew && (
            <div className="border-t border-gray-200 dark:border-gray-700 mt-1">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onCreateNew();
                }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Create New Timeline</span>
              </button>
            </div>
          )}

          {/* Login prompt for unauthenticated users */}
          {showLoginPrompt && (
            <div className="border-t border-gray-200 dark:border-gray-700 mt-1">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onLoginClick?.();
                }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <LogIn className="w-4 h-4" />
                <span>Login to sync timelines</span>
              </button>
              <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-1.5">
                <Cloud className="w-3 h-3" />
                <span>Sync your timelines across devices</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimelineSwitcher;
